import React from 'react';
import { Card, Field, TextField, CssColorField, SaveButton, AnchorField } from '../AdminUI';
import { useSite } from '../../../context/SiteContext';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import MediaUpload from '../MediaUpload';

export default function OriginalsSection() {
  const { data, update } = useSite();
  const [tabs, setTabs] = React.useState(data.originalsTabs || []);
  const [games, setGames] = React.useState(data.originalsGames || []);

  // Sync from server data only on mount; otherwise live polling would
  // overwrite the admin's in-progress edits (e.g. a freshly uploaded video).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setTabs(data.originalsTabs || []); setGames(data.originalsGames || []); }, []);

  const save = () => {
    update((prev) => ({ ...prev, originalsTabs: tabs, originalsGames: games }));
    toast.success('Saved successfully');
  };

  const updGame = (i, k, v) => setGames(games.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const addGame = () => setGames([...games, { id: Date.now(), name: 'New Platform', category: 'CATEGORY', gradient: 'linear-gradient(180deg, #6b1a8b 0%, #2d0e3a 100%)', icon: 'Server', rating: 5.0, image: '', video: '', link: '' }]);
  const remGame = (i) => setGames(games.filter((_, idx) => idx !== i));

  return (
    <>
      <Card title="Showcase Tabs" desc="Tabs shown above platform thumbnails." footer={<SaveButton onClick={save} />}>
        <div className="flex flex-wrap gap-2">
          {tabs.map((t, i) => {
            // Backward-compat: tab can be plain string or { label, link }
            const label = typeof t === 'string' ? t : (t?.label || '');
            const link = typeof t === 'object' ? (t?.link || '') : '';
            const tabSlug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const setLabel = (v) => setTabs(tabs.map((x, idx) => idx === i ? { label: v, link } : x));
            const setLink  = (v) => setTabs(tabs.map((x, idx) => idx === i ? { label, link: v } : x));
            return (
            <div key={i} className="flex flex-col gap-1 bg-zinc-900 border border-zinc-800 rounded-md p-2 min-w-[220px]">
              <div className="flex items-center gap-1.5">
                <input value={label} placeholder="Tab label" onChange={(e) => setLabel(e.target.value)} className="bg-transparent border-none focus:outline-none text-sm text-zinc-100 flex-1" />
                <button onClick={() => setTabs(tabs.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
              </div>
              <input value={link} placeholder="Tab link or #anchor (optional)" onChange={(e) => setLink(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[11px] text-zinc-200 focus:outline-none focus:border-[color:var(--gold)]/50 font-mono" />
              {tabSlug && <AnchorField anchor={`tab-${tabSlug}`} label="Tab anchor" />}
            </div>
            );
          })}
          <button onClick={() => setTabs([...tabs, { label: 'New Tab', link: '' }])} className="h-9 px-3 rounded-md border border-dashed border-zinc-700 text-zinc-300 text-sm flex items-center gap-1"><Plus size={12} /> Tab</button>
        </div>
      </Card>

      <Card title="Platform Thumbnails" desc="Featured platforms. Upload image, GIF, or video — videos autoplay. Click goes to your Visit Link." footer={<SaveButton onClick={save} />}>
        <div className="space-y-3">
          {games.map((g, i) => (
            <div key={g.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid={`platform-thumb-${i}`}>
              <AnchorField anchor={`platform-${g.id}`} label="Platform anchor (deep-link)" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <Field label="Name"><TextField value={g.name} onChange={(v) => updGame(i, 'name', v)} /></Field>
                <Field label="Category"><TextField value={g.category} onChange={(v) => updGame(i, 'category', v)} /></Field>
                <Field label="Icon (lucide)"><TextField value={g.icon} onChange={(v) => updGame(i, 'icon', v)} /></Field>
                <Field label="Rating"><TextField value={g.rating ?? ''} onChange={(v) => updGame(i, 'rating', v)} /></Field>
                <Field label="Sale Price (₹) — enables 'Buy Now'" hint="Leave 0 to hide the Buy button"><TextField value={g.price ?? ''} onChange={(v) => updGame(i, 'price', v)} placeholder="e.g. 4999" /></Field>
                <Field label="Visit Link"><TextField value={g.link || ''} onChange={(v) => updGame(i, 'link', v)} placeholder="https://platform.com" /></Field>
                <Field label="Gradient (fallback)"><CssColorField value={g.gradient} onChange={(v) => updGame(i, 'gradient', v)} placeholder="linear-gradient(...)" /></Field>
                <div>
                  <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Thumbnail Image (or GIF)</label>
                  <MediaUpload value={g.image || ''} onChange={(v) => updGame(i, 'image', v)} accept="image" previewClass="h-14 w-12" testid={`platform-image-${i}`} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Video (autoplay loop muted)</label>
                  <MediaUpload value={g.video || ''} onChange={(v) => updGame(i, 'video', v)} accept="media" previewClass="h-14 w-12" testid={`platform-video-${i}`} />
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <button onClick={() => remGame(i)} className="h-8 px-2 rounded-md bg-red-600/15 text-red-300 border border-red-900/40 text-xs flex items-center gap-1"><Trash2 size={12} /> Remove Platform</button>
              </div>
            </div>
          ))}
          <button onClick={addGame} className="w-full h-10 rounded-md border border-dashed border-zinc-700 text-zinc-300 text-sm flex items-center justify-center gap-1.5"><Plus size={14} /> Add Platform</button>
        </div>
      </Card>
    </>
  );
}
